export type Language = "en" | "sv";

export const translations = {
  en: {
    // Common
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    back: "Back",
    home: "Home",
    next: "Next",
    loading: "Loading...",
    copied: "Copied!",
    share: "Share",
    download: "Download",
    send: "Send",
    
    // Navigation
    createParty: "Create Party",
    
    // Index page
    heroTitle: "Kids' Party Planning",
    heroSubtitle: "Made Simple",
    heroDescription: "Create magical birthday parties with easy date voting, guest management, and beautiful invitations.",
    getStarted: "Get Started",
    
    // Create party
    stepInvite: "Invite",
    stepDetails: "Details",
    stepDates: "Dates",
    
    // Step Invite Upload
    inviteUploadTitle: "Create Your Invitation",
    inviteUploadDescription: "Choose a template or upload your own invitation",
    uploadCustom: "Upload Custom",
    useTemplate: "Use Template",
    chooseTemplate: "Choose Template",
    childName: "Child's Name",
    childAge: "Age",
    dateAndTime: "Date & Time",
    location: "Location",
    personalMessage: "Personal Message",
    downloadInvite: "Download Invitation",
    
    // Step Details
    partyDetailsTitle: "Party Details",
    partyTitle: "Party Title",
    partyTitlePlaceholder: "Emma's Birthday Party",
    occasion: "Occasion",
    occasionPlaceholder: "Birthday",
    description: "Description",
    descriptionPlaceholder: "Tell guests about the party...",
    wishlist: "Wishlist",
    wishlistPlaceholder: "What does the birthday child wish for?",
    addWishlistItem: "Add item",
    contactInfo: "Contact Information",
    contactName: "Contact Name",
    contactPhone: "Phone Number",
    rsvpDeadline: "RSVP Deadline",
    theme: "Theme",
    spotifyPlaylist: "Spotify Playlist",
    spotifyPlaceholder: "https://open.spotify.com/playlist/...",
    
    // Step Dates
    proposedDatesTitle: "Proposed Dates",
    proposedDatesDescription: "Add dates that guests can vote on",
    addDate: "Add Date",
    startTime: "Start Time",
    endTime: "End Time",
    createPartyButton: "Create Party",
    
    // Step Success
    partyCreated: "Party Created!",
    partyCreatedDescription: "Your party is ready. Share the link with guests!",
    guestLink: "Guest Link",
    adminLink: "Admin Link",
    copyLink: "Copy Link",
    downloadQR: "Download QR Code",
    goToAdmin: "Go to Admin Dashboard",
    
    // Guest View
    rsvpTitle: "You're Invited!",
    rsvpDescription: "Please respond to the invitation",
    yourName: "Your Name",
    childNameLabel: "Child's Name",
    parentName: "Parent's Name",
    phoneNumber: "Phone Number",
    dietaryRequirements: "Dietary Requirements",
    dietaryPlaceholder: "Allergies, vegetarian, etc.",
    selectAvailability: "Select Your Availability",
    canAttend: "Can attend",
    maybeAttend: "Maybe",
    cantAttend: "Can't attend",
    submitRsvp: "Submit RSVP",
    alreadyResponded: "You have already responded",
    thankYouRsvp: "Thank you for your response!",
    
    // Admin Dashboard
    adminDashboard: "Admin Dashboard",
    guests: "Guests",
    guestsResponded: "responded",
    activities: "Activities",
    shoppingList: "Shopping List",
    budget: "Budget",
    thankYouCards: "Thank You Cards",
    sendInvitations: "Send Invitations",
    sendReminder: "Send Reminder",
    notResponded: "Not responded",
    confirmDate: "Confirm Date",
    dateConfirmed: "Date Confirmed",
    
    // Activity Manager
    addActivity: "Add Activity",
    activityName: "Activity Name",
    activityDescription: "Description",
    duration: "Duration",
    minutes: "minutes",
    
    // Shopping List
    addItem: "Add Item",
    itemName: "Item",
    quantity: "Quantity",
    assignedTo: "Assigned To",
    purchased: "Purchased",
    itemsPurchased: "items purchased",
    
    // Budget Tracker
    addExpense: "Add Expense",
    expenseDescription: "Description",
    amount: "Amount",
    category: "Category",
    paid: "Paid",
    totalBudget: "Total Budget",
    totalPaid: "Total Paid",
    categories: {
      food: "Food & Drinks",
      decoration: "Decoration",
      activities: "Activities",
      presents: "Presents",
      venue: "Venue",
      other: "Other",
    },
    
    // Thank You Cards
    createThankYouCard: "Create Thank You Card",
    thankYouMessage: "Thank You Message",
    selectTemplate: "Select Template",
    shareCard: "Share Card",
    
    // Admin Manager
    addAdmin: "Add Admin",
    adminName: "Name",
    adminPhone: "Phone",
    sendAdminLink: "Send Admin Link",
    
    // Guest Manager
    invitedGuests: "Invited Guests",
    confirmedGuests: "Confirmed Guests",
    pendingGuests: "Pending Responses",
    searchPreviousGuests: "Search previous guests...",
    noMatches: "No matches",
    importFromPhonebook: "Import from phonebook",
    phonebookChromeOnly: "Contact import requires Chrome on Android",
    addManually: "Add manually",
    
    // Date voting
    votes: "votes",
    bestDate: "Best Date",
    
    // Invite Templates
    templates: {
      balloons: "Classic Balloons",
      princess: "Princess Dreams",
      superhero: "Superhero Power",
      dinosaur: "Dinosaur Adventure",
      unicorn: "Unicorn Magic",
      space: "Space Adventure",
      underwater: "Underwater World",
      safari: "Safari Party",
    },
  },
  sv: {
    // Common
    save: "Spara",
    cancel: "Avbryt",
    delete: "Ta bort",
    edit: "Redigera",
    add: "Lägg till",
    back: "Tillbaka",
    home: "Hem",
    next: "Nästa",
    loading: "Laddar...",
    copied: "Kopierat!",
    share: "Dela",
    download: "Ladda ner",
    send: "Skicka",
    
    // Navigation
    createParty: "Skapa Kalas",
    
    // Index page
    heroTitle: "Barnkalas-planering",
    heroSubtitle: "På ett enkelt sätt",
    heroDescription: "Skapa magiska födelsedagskalas med enkel datumröstning, gästhantering och vackra inbjudningar.",
    getStarted: "Kom igång",
    
    // Create party
    stepInvite: "Inbjudan",
    stepDetails: "Detaljer",
    stepDates: "Datum",
    
    // Step Invite Upload
    inviteUploadTitle: "Skapa din inbjudan",
    inviteUploadDescription: "Välj en mall eller ladda upp en egen inbjudan",
    uploadCustom: "Ladda upp egen",
    useTemplate: "Använd mall",
    chooseTemplate: "Välj mall",
    childName: "Barnets namn",
    childAge: "Ålder",
    dateAndTime: "Datum & tid",
    location: "Plats",
    personalMessage: "Personligt meddelande",
    downloadInvite: "Ladda ner inbjudan",
    
    // Step Details
    partyDetailsTitle: "Kalasdetaljer",
    partyTitle: "Titel",
    partyTitlePlaceholder: "Emmas Födelsedagskalas",
    occasion: "Tillfälle",
    occasionPlaceholder: "Födelsedag",
    description: "Beskrivning",
    descriptionPlaceholder: "Berätta om kalaset...",
    wishlist: "Önskelista",
    wishlistPlaceholder: "Vad önskar sig födelsedagsbarnet?",
    addWishlistItem: "Lägg till önskning",
    contactInfo: "Kontaktinformation",
    contactName: "Kontaktperson",
    contactPhone: "Telefonnummer",
    rsvpDeadline: "Sista svarsdag",
    theme: "Tema",
    spotifyPlaylist: "Spotify-spellista",
    spotifyPlaceholder: "https://open.spotify.com/playlist/...",
    
    // Step Dates
    proposedDatesTitle: "Föreslagna datum",
    proposedDatesDescription: "Lägg till datum som gästerna kan rösta på",
    addDate: "Lägg till datum",
    startTime: "Starttid",
    endTime: "Sluttid",
    createPartyButton: "Skapa kalas",
    
    // Step Success
    partyCreated: "Kalaset är skapat!",
    partyCreatedDescription: "Ditt kalas är redo. Dela länken med gästerna!",
    guestLink: "Gästlänk",
    adminLink: "Adminlänk",
    copyLink: "Kopiera länk",
    downloadQR: "Ladda ner QR-kod",
    goToAdmin: "Gå till admin-dashboard",
    
    // Guest View
    rsvpTitle: "Du är inbjuden!",
    rsvpDescription: "Vänligen svara på inbjudan",
    yourName: "Ditt namn",
    childNameLabel: "Barnets namn",
    parentName: "Förälders namn",
    phoneNumber: "Telefonnummer",
    dietaryRequirements: "Kostönskemål",
    dietaryPlaceholder: "Allergier, vegetarian, etc.",
    selectAvailability: "Välj din tillgänglighet",
    canAttend: "Kan komma",
    maybeAttend: "Kanske",
    cantAttend: "Kan inte komma",
    submitRsvp: "Skicka svar",
    alreadyResponded: "Du har redan svarat",
    thankYouRsvp: "Tack för ditt svar!",
    
    // Admin Dashboard
    adminDashboard: "Admin-dashboard",
    guests: "Gäster",
    guestsResponded: "har svarat",
    activities: "Aktiviteter",
    shoppingList: "Inköpslista",
    budget: "Budget",
    thankYouCards: "Tackkort",
    sendInvitations: "Skicka inbjudningar",
    sendReminder: "Skicka påminnelse",
    notResponded: "Inte svarat",
    confirmDate: "Bekräfta datum",
    dateConfirmed: "Datum bekräftat",
    
    // Activity Manager
    addActivity: "Lägg till aktivitet",
    activityName: "Aktivitetsnamn",
    activityDescription: "Beskrivning",
    duration: "Längd",
    minutes: "minuter",
    
    // Shopping List
    addItem: "Lägg till vara",
    itemName: "Vara",
    quantity: "Antal",
    assignedTo: "Tilldelad",
    purchased: "Köpt",
    itemsPurchased: "varor köpta",
    
    // Budget Tracker
    addExpense: "Lägg till utgift",
    expenseDescription: "Beskrivning",
    amount: "Belopp",
    category: "Kategori",
    paid: "Betald",
    totalBudget: "Total budget",
    totalPaid: "Totalt betalt",
    categories: {
      food: "Mat & Dryck",
      decoration: "Dekoration",
      activities: "Aktiviteter",
      presents: "Presenter",
      venue: "Lokal",
      other: "Övrigt",
    },
    
    // Thank You Cards
    createThankYouCard: "Skapa tackkort",
    thankYouMessage: "Tackmeddelande",
    selectTemplate: "Välj mall",
    shareCard: "Dela kort",
    
    // Admin Manager
    addAdmin: "Lägg till admin",
    adminName: "Namn",
    adminPhone: "Telefon",
    sendAdminLink: "Skicka adminlänk",
    
    // Guest Manager
    invitedGuests: "Inbjudna gäster",
    confirmedGuests: "Bekräftade gäster",
    pendingGuests: "Väntar på svar",
    searchPreviousGuests: "Sök bland tidigare gäster...",
    noMatches: "Inga matchningar",
    importFromPhonebook: "Importera från telefonbok",
    phonebookChromeOnly: "Kontaktimport kräver Chrome på Android",
    addManually: "Lägg till manuellt",
    
    // Date voting
    votes: "röster",
    bestDate: "Bästa datum",
    
    // Invite Templates
    templates: {
      balloons: "Klassiska Ballonger",
      princess: "Prinsessa Drömmar",
      superhero: "Superhjälte Kraft",
      dinosaur: "Dinosaurie Äventyr",
      unicorn: "Enhörning Magi",
      space: "Rymdäventyr",
      underwater: "Undervattensvärld",
      safari: "Safari Fest",
    },
  },
} as const;

export type TranslationKey = keyof typeof translations.en;
